import {
  Box,
  Accordion,
  AccordionSummary,
  Stack,
  Typography,
  AccordionDetails,
} from '@mui/material';
import { ReactNode, useEffect, useState, useId } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';

type AccordionSectionProps = {
  id?: string;
  title: ReactNode;
  children: ReactNode;
  subtitle?: ReactNode;
  compact?: boolean;
  childSectionIds?: readonly string[];
};

const AccordionSection = ({
  id,
  title,
  children,
  subtitle,
  compact = false,
  childSectionIds,
}: AccordionSectionProps) => {
  const generatedId = useId();
  const sectionId = id ?? generatedId;
  const location = useLocation();
  const isHashTarget =
    (Boolean(id) && location.hash === `#${id}`) ||
    Boolean(childSectionIds?.some(childId => location.hash === `#${childId}`));
  const [expanded, setExpanded] = useState(isHashTarget);

  useEffect(() => {
    if (!isHashTarget || !id) {
      return;
    }

    setExpanded(true);
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [id, isHashTarget]);

  return (
    <Box sx={{ mt: 1, scrollMarginTop: 80 }} id={sectionId}>
      <Accordion
        sx={{
          borderRadius: '12px !important',
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
        expanded={expanded}
        onChange={(_event, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary
          id={`${sectionId}-summary`}
          aria-controls={`${sectionId}-content`}
          expandIcon={<ExpandMoreIcon fontSize={compact ? 'small' : 'medium'} />}
          sx={
            compact
              ? {
                  minHeight: 44,
                  '&.Mui-expanded': { minHeight: 44 },
                  '& .MuiAccordionSummary-content': { my: 1.25 },
                  '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.25 },
                }
              : undefined
          }
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 2 }}
            alignItems={{ sm: 'center' }}
          >
            <Typography variant={compact ? 'subtitle2' : 'h6'}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="grey">
                {subtitle}
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails id={`${sectionId}-content`}>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
};
export default AccordionSection;
